<template>
  <div class="dashboard-panel print-break-inside-avoid">
    <div class="flex items-center justify-between mb-4">
      <h3 class="section-title">
        <svg class="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        Portfolio Timeline
      </h3>
      <div class="flex items-center gap-4">
        <div class="flex items-center gap-3 text-xs text-gray-500">
          <span class="flex items-center gap-1"><span class="inline-block w-3 h-3 rounded-sm bg-[#10b981]"></span> On Track</span>
          <span class="flex items-center gap-1"><span class="inline-block w-3 h-3 rounded-sm bg-[#f59e0b]"></span> At Risk</span>
          <span class="flex items-center gap-1"><span class="inline-block w-3 h-3 rounded-sm bg-[#ef4444]"></span> Delayed</span>
        </div>
        <label class="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
          <input
            type="checkbox"
            v-model="quarterly"
            class="w-4 h-4 accent-header cursor-pointer"
          />
          Quarterly view
        </label>
      </div>
    </div>
    <div :class="needsScroll ? 'overflow-x-auto' : 'overflow-x-visible'">
      <div :style="{ height: chartHeight, minWidth: needsScroll ? '800px' : 'auto' }">
        <v-chart :key="chartHeight" class="w-full h-full" :option="chartOption" autoresize />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import * as echarts from 'echarts'
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { CustomChart, LineChart } from 'echarts/charts'
import { GridComponent, TooltipComponent, MarkLineComponent } from 'echarts/components'
import VChart from 'vue-echarts'

use([CanvasRenderer, CustomChart, LineChart, GridComponent, TooltipComponent, MarkLineComponent])

const props = defineProps({
  items: { type: Array, default: () => [] }
})

const quarterly = ref(false)

const ROW_HEIGHT = 36 // px per row — consistent regardless of dev count

const devCount = computed(() => {
  const validItems = props.items.filter(i => i.planned_start && i.planned_finish)
  return new Set(validItems.map(i => i.development)).size
})

// Only force min-width scroll when there are many developments
const needsScroll = computed(() => devCount.value > 2)

const chartHeight = computed(() => {
  const validItems = props.items.filter(i => i.planned_start && i.planned_finish)
  const rowCount = validItems.length + devCount.value // workstream rows + header rows
  // Grid top (20) + bottom (50) + x-axis labels + safety buffer
  return `${Math.max(rowCount * ROW_HEIGHT + 130, 180)}px`
})

const statusColors = {
  'On Track': '#10b981',
  'At Risk': '#f59e0b',
  'Delayed': '#ef4444'
}

const chartOption = computed(() => {
  const validItems = [...props.items].filter(item => item.planned_start && item.planned_finish)

  // Group items by development, preserving order of first appearance
  const devOrder = []
  const grouped = {}
  validItems.forEach(item => {
    if (!grouped[item.development]) {
      grouped[item.development] = []
      devOrder.push(item.development)
    }
    grouped[item.development].push(item)
  })

  // Build flat display rows: header row (no bar) + workstream rows per development
  // ECharts renders bottom-to-top so we reverse the final list
  const rows = []
  devOrder.forEach(dev => {
    rows.push({ type: 'header', development: dev, label: dev })
    grouped[dev].forEach(item => {
      rows.push({ type: 'workstream', ...item, label: item.workstream })
    })
  })
  const displayRows = [...rows].reverse()

  // Use index-based unique keys to avoid ECharts collapsing duplicate workstream names
  const categories = displayRows.map((r, i) => `__row_${i}__`)

  const allTimes = validItems.flatMap(item => [
    new Date(item.planned_start).getTime(),
    new Date(item.forecast_finish || item.planned_finish).getTime()
  ])
  const minTime = allTimes.length ? Math.min(...allTimes) : Date.now()
  const maxTime = allTimes.length ? Math.max(...allTimes) : Date.now() + 86400000

  const renderGanttItem = (params, api) => {
    const index = api.value(0)
    // Use actual ECharts slot height — reliable source of truth for spacing
    const slotHeight = api.size([0, 1])[1]
    const GAP = 6 // px gap above and below bar within its slot
    const barH = Math.min(ROW_HEIGHT - GAP * 2, slotHeight - GAP * 2, 20)

    // Header rows: full-width blue band filling the slot
    if (api.value(4) === 1) {
      const y = api.coord([0, index])
      const bandY = y[1] - slotHeight / 2
      return {
        type: 'group',
        children: [
          {
            type: 'rect',
            shape: {
              x: params.coordSys.x,
              y: bandY,
              width: params.coordSys.width,
              height: slotHeight
            },
            style: { fill: '#dbeafe' }
          },
          {
            type: 'line',
            shape: {
              x1: params.coordSys.x,
              y1: bandY,
              x2: params.coordSys.x + params.coordSys.width,
              y2: bandY
            },
            style: { stroke: '#93c5fd', lineWidth: 1.5 }
          }
        ]
      }
    }

    const start = api.coord([api.value(1), index])
    const end = api.coord([api.value(2), index])
    const barWidth = Math.max(4, end[0] - start[0])
    const rectShape = echarts.graphic.clipRectByRect({
      x: start[0],
      y: start[1] - barH / 2,
      width: barWidth,
      height: barH
    }, {
      x: params.coordSys.x,
      y: params.coordSys.y,
      width: params.coordSys.width,
      height: params.coordSys.height
    })
    return rectShape && {
      type: 'rect',
      transition: ['shape'],
      shape: { ...rectShape, r: 3 },
      style: api.style({ fill: api.value(3) })
    }
  }

  const today = new Date().getTime()

  return {
    tooltip: {
      formatter: (params) => {
        const row = displayRows[params.value[0]]
        if (row.type === 'header') return `<b>${row.development}</b>`
        const fmt = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'
        const forecastDiff = row.forecast_finish && row.forecast_finish !== row.planned_finish
          ? `<br/><span style="color:#ef4444">Forecast finish: ${fmt(row.forecast_finish)}</span>`
          : ''
        const pct = row.percent_complete != null ? `<br/>Complete: <b>${Math.round(row.percent_complete)}%</b>` : ''
        return `<b>${row.development}</b><br/>${row.workstream}<br/>Status: <b>${row.status}</b><br/>Start: ${fmt(row.planned_start)}<br/>Planned finish: ${fmt(row.planned_finish)}${forecastDiff}${pct}`
      }
    },
    grid: {
      left: 250,
      right: 40,
      top: 20,
      bottom: 50
    },
    xAxis: quarterly.value
      ? {
          type: 'time',
          min: minTime,
          max: maxTime,
          minInterval: 3 * 30 * 24 * 3600 * 1000,
          maxInterval: 3 * 31 * 24 * 3600 * 1000,
          axisLabel: {
            formatter: (value) => {
              const d = new Date(value)
              const q = Math.floor(d.getMonth() / 3) + 1
              return `Q${q} ${d.getFullYear()}`
            }
          }
        }
      : {
          type: 'time',
          min: minTime,
          max: maxTime,
          axisLabel: { formatter: '{MMM} {yyyy}' }
        },
    yAxis: {
      type: 'category',
      data: categories,
      axisLabel: {
        width: 230,
        overflow: 'truncate',
        interval: 0,
        // Bold the development header labels
        rich: {
          header: { fontWeight: 'bold', color: '#1e3a5f' },
          workstream: { color: '#374151', padding: [0, 0, 0, 12] }
        },
        formatter: (value) => {
          const idx = parseInt(value.replace('__row_', '').replace('__', ''), 10)
          const row = displayRows[idx]
          if (row?.type === 'header') return `{header|${row.label}}`
          return `{workstream|  ${row?.label ?? value}}`
        }
      }
    },
    series: [{
      type: 'custom',
      renderItem: renderGanttItem,
      itemStyle: { opacity: 0.85 },
      encode: { x: [1, 2], y: 0 },
      data: displayRows.map((row, index) => ({
        value: [
          index,
          row.type === 'header' ? minTime : new Date(row.planned_start).getTime(),
          row.type === 'header' ? minTime : new Date(row.forecast_finish || row.planned_finish).getTime(),
          row.type === 'header' ? 'transparent' : (statusColors[row.status] || '#6b7280'),
          row.type === 'header' ? 1 : 0  // flag: 1=header (no bar), 0=workstream
        ],
        itemStyle: { color: row.type === 'header' ? 'transparent' : (statusColors[row.status] || '#6b7280') }
      })),
      markLine: {
        symbol: ['none', 'none'],
        label: {
          formatter: 'TODAY',
          position: 'end',
          color: '#2563eb',
          fontWeight: 'bold',
          fontSize: 11,
          backgroundColor: '#eff6ff',
          padding: [2, 4],
          borderRadius: 3
        },
        lineStyle: { color: '#2563eb', type: 'dashed', width: 1.5 },
        data: [{ xAxis: today }]
      }
    }]
  }
})
</script>
